<script setup lang="ts">
import { faListUl, faLockKeyhole, faSearch } from '@fortawesome/pro-light-svg-icons'

const props = withDefaults(defineProps<{
  title?: string
  showSearchOption?: boolean
  showContentOption?: boolean
  showExpandControls?: boolean
}>(), {
  showContentOption: true,
})

const searchMode = defineModel<boolean>('search-mode', {
  default: false,
})

const hasLicense = useHasLicense()
const { allExpanded, expandableIds, expandAll, collapseAll } = useTocExpandController()

const expandAllModel = computed({
  get: () => allExpanded.value,
  set: (value: boolean) => value ? expandAll() : collapseAll(),
})

const hasExpandableSections = computed(() => expandableIds.value.size > 0)

// Bound to both the visible text and the accessible name, so WCAG 2.5.3 cannot drift.
const EXPAND_ALL_LABEL = 'Åpne alle kapitler'

const contentTitle = computed(() => props.title || 'Innhold')

</script>

<template>
  <div class="flex flex-col gap-2xs">
    <div class="flex gap-2xs items-center">
      <Button
        v-if="showSearchOption && showContentOption !== false"
        size="small"
        :class="{ 'bg-surface-primary dark:bg-surface-hover': !searchMode }"
        class="flex items-center px-xs gap-2xs rounded-lg hover:bg-blurple-50 dark:hover:bg-surface-hover dark:text-carbon-50 focus:outline-none focus:ring-blurple-300"
        aria-label="Innhold"
        @click="searchMode = false"
      >
        <Icon :icon="faListUl" />
        {{ contentTitle }}
      </Button>
      <Button
        v-if="showSearchOption"
        size="small"
        :class="{ 'bg-surface-primary dark:bg-surface-hover': searchMode, 'disabled:bg-transparent': !hasLicense }"
        class="flex items-center px-xs gap-2xs rounded-lg hover:bg-blurple-50 dark:hover:bg-surface-hover dark:text-carbon-50 focus:outline-none focus:ring-blurple-300"
        aria-label="Søk"
        :disabled="!hasLicense"
        @click="searchMode = true"
      >
        <Icon :icon="hasLicense ? faSearch : faLockKeyhole" />
        Søk
      </Button>
      <div v-if="!showSearchOption" class="text-small font-medium dark:text-white">
        {{ contentTitle }}
      </div>
    </div>

    <div v-if="showExpandControls && !searchMode" class="flex items-center gap-xs px-xs py-3xs">
      <FormSwitch
        v-model="expandAllModel"
        name="toc-expand-all"
        label="visning av alle kapitler"
        :aria-label="EXPAND_ALL_LABEL"
        :disabled="!hasExpandableSections"
      />
      <!-- Hidden from screen readers because the switch already has the same `aria-label`. -->
      <label
        for="toc-expand-all"
        aria-hidden="true"
        class="text-small select-none font-normal dark:text-carbon-50"
        :class="hasExpandableSections ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'"
      >
        {{ EXPAND_ALL_LABEL }}
      </label>
    </div>
  </div>
</template>
